/* Product page: variant choice, quantity, Add to bag, the photo lightbox (loaded on first open), the phone gallery
   dots, the phone sticky bar, and the "Recently viewed" list this browser keeps.
   Add to bag (the main button and the sticky bar submit the same form) posts it to Shopify's standard /cart/add.js
   through LMB.cart in assets/cart.js, which updates and opens the bag (no toast here), so the product_added_to_cart
   customer event fires for every pixel. Without JavaScript the form posts to /cart/add.
   Only choosing a size changes the address (?variant=); the gallery dots and the sticky bar never do. */
if (!customElements.get('lmb-product')) {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const t = (key, fallback) => (window.LMB && LMB.strings && LMB.strings[key]) || fallback;
  const PHONE = window.matchMedia('(max-width: 749px)');
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Formats cents with the shop's money format, dropping ".00" like money_without_trailing_zeros. */
  const formatMoney = (cents, format) => {
    const value = (cents / 100).toFixed(2);
    const [whole, decimals] = value.split('.');
    const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const withDots = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    const trim = (amount, sep) => (decimals === '00' ? amount : `${amount}${sep}${decimals}`);
    return (format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return withCommas;
        case 'amount_with_comma_separator':
          return trim(withDots, ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDots;
        default:
          return trim(withCommas, '.');
      }
    });
  };

  /* Loads the lightbox script and stylesheet once, on the first open. */
  let lightboxPromise = null;
  const loadLightbox = (scriptSrc, styleHref) => {
    if (window.LMB && LMB.lightbox) return Promise.resolve();
    if (!lightboxPromise) {
      const load = (el) =>
        new Promise((resolve, reject) => {
          el.onload = resolve;
          el.onerror = reject;
          document.head.appendChild(el);
        });
      const style = Object.assign(document.createElement('link'), { rel: 'stylesheet', href: styleHref });
      const script = Object.assign(document.createElement('script'), { src: scriptSrc });
      lightboxPromise = Promise.all([load(style), load(script)]).catch((error) => {
        lightboxPromise = null;
        throw error;
      });
    }
    return lightboxPromise;
  };

  customElements.define(
    'lmb-product',
    class LmbProduct extends HTMLElement {
      connectedCallback() {
        this.form = document.getElementById(this.dataset.formId);
        this.addButton = this.querySelector('[data-add-button]');
        this.stickyButton = this.querySelector('[data-sticky-add]');
        this.qtyInput = this.querySelector('[data-qty-input]');
        this.status = this.querySelector('[data-form-status]');
        const variantsScript = this.querySelector('[data-variants]');
        this.variants = variantsScript ? JSON.parse(variantsScript.textContent) : null;
        this.variant = null;
        this.unitPrice = this.readUnitPrice();

        this.addEventListener('click', (event) => {
          const step = event.target.closest('[data-qty-step]');
          if (step) this.stepQuantity(Number(step.dataset.qtyStep));
          const zoom = event.target.closest('[data-lightbox]');
          if (zoom) this.openLightbox(Number(zoom.dataset.lightbox), zoom);
          const dot = event.target.closest('[data-gallery-dot]');
          if (dot) this.showMedia(Number(dot.dataset.galleryDot));
        });
        this.addEventListener('change', (event) => {
          if (event.target.matches('[data-option-index]')) this.onOptionChange();
          if (event.target === this.qtyInput) this.updateAddPrice();
        });
        // A size's radio is hidden at the start of its row, so the browser only scrolls that one spot into view: bring
        // the whole tile in, clear of the sticky bar (the page's scroll-padding), when the keyboard lands on it.
        this.addEventListener('focusin', (event) => {
          const input = event.target;
          if (!input.matches('[data-option-index]') || !input.labels || !input.labels[0]) return;
          input.labels[0].scrollIntoView({ block: 'nearest' });
        });
        if (this.qtyInput) this.qtyInput.addEventListener('input', () => this.updateAddPrice());
        if (this.form) this.form.addEventListener('submit', (event) => this.onSubmit(event));

        this.rememberView();
        this.initGallery();
        this.initSticky();
      }

      disconnectedCallback() {
        // Forgotten, not just stopped: put back in the page (a section moved in the theme editor), the next
        // connect must build a new watcher, or the button is never watched again.
        if (this.stickyObserver) this.stickyObserver.disconnect();
        this.stickyObserver = null;
        this.stickyTop = undefined;
        if (this.onViewportChange) window.removeEventListener('resize', this.onViewportChange);
        if (this.onPhoneChange) PHONE.removeEventListener('change', this.onPhoneChange);
        // The theme editor re-renders the section: the new copy decides again.
        document.documentElement.classList.remove('has-sticky-atc');
      }

      /* ---------- Recently viewed: this piece goes first in the list the homepage row reads ---------- */
      /* localStorage 'lmb:recent': product handles, newest first, no repeats, at most 12 (read by
         assets/recently-viewed.js). Private windows and blocked storage just keep no list. */
      rememberView() {
        const handle = this.dataset.productHandle;
        if (!handle) return;
        try {
          const k = 'lmb:recent';
          const stored = JSON.parse(localStorage.getItem(k) || '[]');
          const list = (Array.isArray(stored) ? stored : []).filter((h) => h !== handle);
          list.unshift(handle);
          localStorage.setItem(k, JSON.stringify(list.slice(0, 12)));
        } catch (e) {
          /* No storage: nothing to remember. */
        }
      }

      readUnitPrice() {
        if (this.variants) {
          const id = Number(this.form && this.form.querySelector('[data-variant-id]').value);
          const variant = this.variants.find((v) => v.id === id);
          if (variant) return variant.price;
        }
        const el = this.querySelector('[data-unit-price]');
        return el ? Number(el.dataset.unitPrice) : null;
      }

      get quantity() {
        const value = this.qtyInput ? parseInt(this.qtyInput.value, 10) : 1;
        return Number.isFinite(value) && value > 0 ? value : 1;
      }

      stepQuantity(delta) {
        if (!this.qtyInput) return;
        const max = Number(this.qtyInput.max) || 99;
        this.qtyInput.value = Math.min(max, Math.max(1, this.quantity + delta));
        this.updateAddPrice();
      }

      updateAddPrice() {
        const priceEl = this.querySelector('[data-add-price]');
        if (!priceEl || this.unitPrice == null) return;
        const total = formatMoney(this.unitPrice * this.quantity, this.dataset.moneyFormat);
        priceEl.firstChild.textContent = `${total} `;
      }

      /* ---------- Variants ---------- */
      onOptionChange() {
        if (!this.variants) return;
        const picker = this.querySelector('[data-variant-picker]');
        const selected = [];
        picker.querySelectorAll('[data-option-index]:checked').forEach((input) => {
          selected[Number(input.dataset.optionIndex)] = input.value;
        });
        const variant = this.variants.find((v) => v.options.every((value, i) => value === selected[i]));
        this.setVariant(variant);
      }

      setVariant(variant) {
        const idInput = this.form && this.form.querySelector('[data-variant-id]');
        const available = !!(variant && variant.available);
        if (variant && idInput) idInput.value = variant.id;
        if (variant) {
          this.unitPrice = variant.price;
          const price = this.querySelector('[data-price]');
          if (price) price.innerHTML = variant.price_html;
          const sticky = this.querySelector('[data-sticky-price]');
          if (sticky) sticky.innerHTML = variant.price_html;
          const url = new URL(window.location.href);
          url.searchParams.set('variant', variant.id);
          window.history.replaceState(window.history.state, '', url.toString());
        }
        const label = available ? t('add_to_bag', 'Add to bag') : variant ? t('sold_out', 'Sold out') : t('unavailable', 'Unavailable');
        [this.addButton, this.stickyButton].forEach((button) => {
          if (!button) return;
          button.disabled = !available;
          const text = button.querySelector('[data-add-label]') || button;
          text.textContent = label;
        });
        const dynamic = this.querySelector('[data-dynamic-checkout]');
        if (dynamic) dynamic.classList.toggle('is-hidden', !available);
        // The "−11%" badge and its spoken "11% off" follow the price: unchanged when the options match no variant (the
        // price above stays too).
        if (variant) this.setPercentOff(Number(variant.percent_off) || 0);
        this.updateAddPrice();
      }

      setPercentOff(percent) {
        const badge = this.querySelector('[data-percent-badge]');
        if (badge) {
          badge.hidden = !(percent > 0);
          if (percent > 0) badge.textContent = badge.dataset.template.replace('[percent]', percent);
        }
        const spoken = this.querySelector('[data-percent-off]');
        if (spoken) spoken.textContent = percent > 0 ? spoken.dataset.template.replace('[percent]', percent) : '';
      }

      /* ---------- Add to bag ---------- */
      async onSubmit(event) {
        event.preventDefault();
        if (!this.addButton || this.addButton.disabled || this.addButton.getAttribute('aria-busy') === 'true') return;
        const buttons = [this.addButton, this.stickyButton].filter(Boolean);
        buttons.forEach((b) => b.setAttribute('aria-busy', 'true'));
        this.setStatus('');
        try {
          const submitter = event.submitter && event.submitter !== this.addButton ? event.submitter : this.addButton;
          if (window.LMB && LMB.cart) {
            // assets/cart.js: /cart/add.js with the bag rendered in the same request; opens the bag.
            await LMB.cart.add(new FormData(this.form), { trigger: submitter });
          } else {
            const response = await fetch(`${root}cart/add.js`, {
              method: 'POST',
              headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
              body: new FormData(this.form),
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.description || data.message || t('quick_add_error', 'Could not add this piece.'));
            const cart = await (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json();
            if (window.LMB && LMB.updateCartCount) LMB.updateCartCount(cart.item_count);
          }
          this.setStatus(t('quick_add_added', 'Added to bag'));
          buttons.forEach((b) => b.classList.add('is-added'));
          setTimeout(() => buttons.forEach((b) => b.classList.remove('is-added')), 1800);
        } catch (error) {
          this.setStatus(error.message, true);
        } finally {
          buttons.forEach((b) => b.removeAttribute('aria-busy'));
        }
      }

      setStatus(message, isError = false) {
        if (!this.status) return;
        this.status.textContent = message;
        this.status.classList.toggle('is-error', isError);
      }

      /* ---------- Lightbox: its script and styles load on the first open only ---------- */
      async openLightbox(start, trigger) {
        if (!this.dataset.lightboxScript || this.lightboxLoading) return;
        this.lightboxLoading = true;
        try {
          await loadLightbox(this.dataset.lightboxScript, this.dataset.lightboxStyle);
        } catch (error) {
          return;
        } finally {
          this.lightboxLoading = false;
        }
        const items = Array.from(this.querySelectorAll('[data-lightbox]')).map((button) => {
          const image = button.querySelector('img');
          return {
            srcset: button.dataset.srcset,
            width: Number(button.dataset.width),
            height: Number(button.dataset.height),
            alt: image ? image.alt : '',
          };
        });
        let strings = {};
        try {
          strings = JSON.parse(this.dataset.lightboxStrings || '{}');
        } catch (error) {
          /* default labels */
        }
        LMB.lightbox.open({ items, start, trigger, strings, scheme: this.dataset.lightboxScheme });
      }

      /* ---------- Phone gallery: the dots follow the swipe, and a tap on one swipes to its photo ---------- */
      initGallery() {
        this.gallery = this.querySelector('[data-gallery]');
        this.dots = Array.from(this.querySelectorAll('[data-gallery-dot]'));
        if (!this.gallery || !this.dots.length) return;
        this.slides = Array.from(this.gallery.querySelectorAll('[data-media-index]'));
        let current = 0;
        let frame = null;
        const update = () => {
          frame = null;
          // The photo whose left edge is nearest the row's left edge is the one in view.
          const left = this.gallery.getBoundingClientRect().left;
          let index = 0;
          let best = Infinity;
          this.slides.forEach((slide, i) => {
            const distance = Math.abs(slide.getBoundingClientRect().left - left);
            if (distance < best) {
              best = distance;
              index = i;
            }
          });
          if (index === current) return;
          current = index;
          this.dots.forEach((dot, i) => {
            if (i === index) dot.setAttribute('aria-current', 'true');
            else dot.removeAttribute('aria-current');
          });
        };
        this.gallery.addEventListener(
          'scroll',
          () => {
            if (!frame) frame = requestAnimationFrame(update);
          },
          { passive: true }
        );
      }

      /* Scrolls the row itself (never the page, never the address bar); the scroll then moves the dots. */
      showMedia(index) {
        if (!this.gallery || !this.slides || !this.slides[index]) return;
        this.gallery.scrollTo({
          left: this.slides[index].offsetLeft - this.slides[0].offsetLeft,
          behavior: REDUCED_MOTION.matches ? 'auto' : 'smooth',
        });
      }

      /* ---------- Phone sticky bar: shown whenever the main Add to bag button is off screen ---------- */
      /* "On screen" means wholly in view below the pinned header (--header-height, assets/theme.js). So the bar shows
         as soon as the button starts to slide under the header, and whenever any of it is below the screen's bottom
         edge, which includes a first screen that ends above it (Safari shows about 660–750 px of an 852 px phone).
         The bar is taller than the button, so a button cut by the bottom edge is always wholly under the bar: there
         is always exactly one Add to bag in view. Both buttons submit the same form. */
      initSticky() {
        this.stickyBar = this.querySelector('[data-sticky-atc]');
        if (!this.stickyBar || !this.addButton || !('IntersectionObserver' in window)) return;
        this.stickyShown = false;
        this.buyInView = true;
        this.watchBuy();
        let frame = null;
        this.onViewportChange = () => {
          if (frame) return;
          frame = requestAnimationFrame(() => {
            frame = null;
            this.watchBuy();
          });
        };
        window.addEventListener('resize', this.onViewportChange);
        this.onPhoneChange = () => this.showSticky(PHONE.matches && !this.buyInView);
        PHONE.addEventListener('change', this.onPhoneChange);
        // The header's pinned height settles once the fonts are in (the chips' widths): look again after its update.
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(() => requestAnimationFrame(() => requestAnimationFrame(this.onViewportChange)));
        }
      }

      /* (Re)starts watching the button below the header's current height. A new watcher reports at once, so it is
         only replaced when that height has changed. */
      watchBuy() {
        if (!this.isConnected) return;
        const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height'));
        const top = Math.round(header || 0);
        if (this.stickyObserver && top === this.stickyTop) return;
        if (this.stickyObserver) this.stickyObserver.disconnect();
        this.stickyTop = top;
        this.stickyObserver = new IntersectionObserver(
          (entries) => {
            // Several reports can queue up during a fast scroll: the last one is where the button is now. 0.99, not 1:
            // sub-pixel rounding can keep a button that is wholly in view just under 1.
            const entry = entries[entries.length - 1];
            this.buyInView = entry.intersectionRatio >= 0.99;
            this.showSticky(PHONE.matches && !this.buyInView);
          },
          // Several steps, so every way in and out is reported, however fast the scroll.
          { rootMargin: `${-top}px 0px 0px 0px`, threshold: [0, 0.25, 0.5, 0.75, 0.99, 1] }
        );
        this.stickyObserver.observe(this.addButton);
      }

      showSticky(value) {
        if (value === this.stickyShown) return;
        this.stickyShown = value;
        const bar = this.stickyBar;
        if (value) {
          bar.hidden = false;
          requestAnimationFrame(() => bar.classList.add('is-visible'));
        } else {
          bar.classList.remove('is-visible');
          setTimeout(() => {
            if (!this.stickyShown) bar.hidden = true;
          }, 260);
        }
        // Lifts the toast above the bar and keeps the footer clear of it.
        document.documentElement.classList.toggle('has-sticky-atc', value);
      }
    }
  );
}
